import React, { useState, useId } from 'react';
import { Language } from '../types';
import { languages } from '../data/languages';

interface LanguageSelectorProps {
  value: string;
  onChange: (code: string) => void;
  label: string;
  id: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ value, onChange, label, id }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const listboxId = useId();
  const searchId = useId();
  
  const selectedLanguage = languages.find(lang => 
    lang.code === value || lang.variants?.some(v => v.code === value)
  );

  const displayName = (() => {
    if (!selectedLanguage) return value;
    
    if (value.includes('-')) {
      const variant = selectedLanguage.variants?.find(v => v.code === value);
      if (variant) return variant.name;
    }
    
    return selectedLanguage.name;
  })();

  const filteredLanguages = languages.filter(language => 
    language.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (language.nativeName && language.nativeName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label ${id}-button`}
        aria-controls={listboxId}
        className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 w-full flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#00A86B] focus:border-[#00A86B]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span id={`${id}-button`} className="truncate">{displayName}</span>
        <svg
          className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          id={listboxId}
          role="listbox"
          aria-labelledby={`${id}-label`}
          className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
        >
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-2 py-2">
            <input
              id={searchId}
              type="search"
              className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A86B] focus:border-[#00A86B]"
              placeholder="Search languages..."
              role="searchbox"
              aria-label="Search languages"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {filteredLanguages.length === 0 ? (
            <div className="py-2 px-4 text-sm text-gray-500 dark:text-gray-400" role="alert">
              No languages found
            </div>
          ) : (
            <ul role="listbox" aria-labelledby={`${id}-label`}>
              {filteredLanguages.map((language) => (
                <React.Fragment key={language.code}>
                  <li
                    role="option"
                    aria-selected={language.code === value}
                    className={`cursor-pointer select-none px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      language.code === value ? 'bg-gray-100 dark:bg-gray-700' : ''
                    }`}
                    onClick={() => {
                      onChange(language.code);
                      setIsOpen(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{language.name}</span>
                      {language.nativeName && language.nativeName !== language.name && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {language.nativeName}
                        </span>
                      )}
                    </div>
                  </li>
                  {language.variants && language.code === value && (
                    <li
                      role="separator"
                      className="pl-6 pr-4 py-1 text-xs text-gray-500 dark:text-gray-400 border-t border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                    >
                      Variants
                    </li>
                  )}
                  {language.variants && (language.code === value || searchTerm) && (
                    language.variants.map(variant => (
                      <li
                        key={variant.code}
                        role="option"
                        aria-selected={variant.code === value}
                        className={`cursor-pointer select-none pl-6 pr-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          variant.code === value ? 'bg-gray-100 dark:bg-gray-700' : ''
                        }`}
                        onClick={() => {
                          onChange(variant.code);
                          setIsOpen(false);
                        }}
                      >
                        {variant.name}
                      </li>
                    ))
                  )}
                </React.Fragment>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;